import {
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
} from 'react';
import clsx from 'clsx';
import './TextInput.css';

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

export interface TextInputProps extends NativeInputProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Slot on the right side of the input (e.g. clear button). */
  trailing?: ReactNode;
  /** CSS class on the outer field wrapper (not the input itself). */
  fieldClassName?: string;
  ref?: Ref<HTMLInputElement>;
}

/**
 * TextInput — the field for single-line text. Same visual frame as
 * Textarea + PasswordInput for consistency across the input family.
 */
export function TextInput({
  label,
  hint,
  error,
  trailing,
  fieldClassName,
  id,
  className,
  disabled,
  required,
  'aria-invalid': ariaInvalid,
  ...rest
}: TextInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const isInvalid = Boolean(error) || ariaInvalid === true || ariaInvalid === 'true';
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div
      className={clsx('fluent-field', fieldClassName)}
      data-invalid={isInvalid || undefined}
      data-disabled={disabled || undefined}
    >
      {label != null && (
        <label className="fluent-field__label" htmlFor={inputId}>
          {label}
          {required && (
            <span aria-hidden="true" style={{ color: 'var(--color-danger)', marginLeft: 2 }}>
              *
            </span>
          )}
        </label>
      )}
      <div className="fluent-field__frame">
        <input
          id={inputId}
          className={clsx('fluent-field__input', className)}
          disabled={disabled}
          required={required}
          aria-invalid={isInvalid || undefined}
          aria-describedby={describedBy}
          {...rest}
        />
        {trailing != null && <div className="fluent-field__trailing">{trailing}</div>}
      </div>
      {hint != null && !error && (
        <span id={hintId} className="fluent-field__hint">
          {hint}
        </span>
      )}
      {error != null && (
        <span id={errorId} className="fluent-field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * PasswordInput — TextInput with a built-in show/hide button.
 * Keeps label, hint, error handling identical to TextInput.
 */
export interface PasswordInputProps extends Omit<TextInputProps, 'type' | 'trailing'> {
  /** Localize the "Show" / "Hide" labels. */
  showLabel?: string;
  hideLabel?: string;
}

export function PasswordInput({ showLabel = 'ჩვენება', hideLabel = 'დამალვა', ...rest }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  return (
    <TextInput
      {...rest}
      type={visible ? 'text' : 'password'}
      autoComplete={rest.autoComplete ?? 'current-password'}
      trailing={
        <button
          type="button"
          className="fluent-field__trailing-btn"
          onClick={() => setVisible((v) => !v)}
          aria-pressed={visible}
        >
          {visible ? hideLabel : showLabel}
        </button>
      }
    />
  );
}
